/**
 * SmartDocsPage Component
 * Main page for the Smart Docs module - Project-scoped documents
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  FileText,
  Trash2,
  ChevronRight,
  Sparkles,
  Calendar,
  LayoutGrid,
  Loader2,
  AlertCircle,
  Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useSmartDocs,
  useSmartDoc,
  useCreateSmartDoc,
  useUpdateSmartDoc,
  useDeleteSmartDoc,
  useMigrateLocalDocs,
  useHasLocalDocs,
  useLocalDocsCount,
} from '@/hooks/useSmartDocs'
import { EntityAwareEditor, type EntityAwareEditorHandle } from './EntityAwareEditor'
import { UnifiedEntitySidebar } from './UnifiedEntitySidebar'
import { TemplatePickerDialog } from './TemplatePickerDialog'
import type { SmartDocument } from '@/types/document'
import type { DocTemplateResponse, DocSection } from '@ispot/shared'
import { useSessionStore } from '@/stores/sessionStore'

interface SmartDocsPageProps {
  initialDocId?: string | null
}

export function SmartDocsPage({ initialDocId }: SmartDocsPageProps = {}) {
  const [currentDocId, setCurrentDocId] = useState<string | null>(initialDocId ?? null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const editorRef = useRef<EntityAwareEditorHandle>(null)

  // Check session context
  const { isReady } = useSessionStore()

  // React Query hooks
  const { data: documents = [], isLoading, error } = useSmartDocs()
  const { data: currentDocument } = useSmartDoc(currentDocId)
  const createDoc = useCreateSmartDoc()
  const updateDoc = useUpdateSmartDoc()
  const deleteDoc = useDeleteSmartDoc()

  // Migration
  const hasLocalDocs = useHasLocalDocs()
  const localDocsCount = useLocalDocsCount()
  const migrateLocalDocs = useMigrateLocalDocs()
  const [migrationDone, setMigrationDone] = useState(false)

  // Auto-migrate localStorage docs on first load
  useEffect(() => {
    if (isReady && hasLocalDocs && !migrationDone && !migrateLocalDocs.isPending) {
      migrateLocalDocs.mutate(undefined, {
        onSuccess: (result) => {
          setMigrationDone(true)
          if (result.migrated > 0) {
            console.log(`Migrated ${result.migrated} documents to backend`)
          }
        },
      })
    }
  }, [isReady, hasLocalDocs, migrationDone, migrateLocalDocs])

  // Handle initialDocId changes
  useEffect(() => {
    if (initialDocId) {
      setCurrentDocId(initialDocId)
    }
  }, [initialDocId])

  // Auto-select first document when list loads (only if no initialDocId)
  useEffect(() => {
    if (!currentDocId && !initialDocId && documents.length > 0) {
      setCurrentDocId(documents[0].id)
    }
  }, [documents, currentDocId, initialDocId])

  const handleCreateDocument = async () => {
    try {
      const doc = await createDoc.mutateAsync({ title: 'Untitled Document' })
      setCurrentDocId(doc.id)
    } catch (err) {
      console.error('Failed to create document:', err)
    }
  }

  const handleDeleteDocument = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return

    try {
      await deleteDoc.mutateAsync(id)
      if (currentDocId === id) {
        setCurrentDocId(documents.find(d => d.id !== id)?.id ?? null)
      }
    } catch (err) {
      console.error('Failed to delete document:', err)
    }
  }

  const handleTitleChange = async (id: string, title: string) => {
    try {
      await updateDoc.mutateAsync({ id, title })
    } catch (err) {
      console.error('Failed to update title:', err)
    }
  }

  // Create document from template
  const handleTemplateSelect = useCallback(async (template: DocTemplateResponse, title: string) => {
    try {
      // Extract sections from template structure
      const structure = template.structure as { sections?: DocSection[] }
      const sections = structure?.sections || []

      // Build TipTap content from template sections
      const tiptapContent = {
        type: 'doc',
        content: sections.flatMap((section) => [
          {
            type: 'heading',
            attrs: { level: section.headingLevel || 2 },
            content: [{ type: 'text', text: section.heading }],
          },
          {
            type: 'paragraph',
            content: section.placeholder
              ? [{ type: 'text', text: section.placeholder }]
              : [],
          },
        ]),
      }

      // Build plain text from sections
      const plainText = sections
        .map((section) => {
          const headingMarker = '#'.repeat(section.headingLevel || 2)
          return `${headingMarker} ${section.heading}\n\n${section.placeholder || ''}`
        })
        .join('\n\n')

      // Create the document
      const doc = await createDoc.mutateAsync({
        title,
        content: tiptapContent,
        plainText,
        templateId: template.id,
        domain: template.domain,
        subType: template.subType,
      })

      setCurrentDocId(doc.id)
      setTemplateDialogOpen(false)
    } catch (err) {
      console.error('Failed to create document from template:', err)
    }
  }, [createDoc])

  // Handle entity click - scroll to entity in editor
  const handleEntityClick = useCallback((entity: { id: string; evidence: string }) => {
    if (editorRef.current && entity.evidence) {
      editorRef.current.scrollToEntity(entity.id, entity.evidence)
    }
  }, [])

  // Handle heading click - scroll to heading in editor
  const handleHeadingClick = useCallback((headingText: string) => {
    if (editorRef.current) {
      editorRef.current.scrollToHeading(headingText)
    }
  }, [])

  // Show loading state
  if (!isReady) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-text-muted">
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p>Loading project...</p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-red-500">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p>Failed to load documents</p>
          <p className="text-sm text-text-muted">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex">
      {/* Document List Sidebar */}
      <div className="w-64 bg-warm-50 border-r border-warm-200 flex flex-col">
        <div className="p-4 border-b border-warm-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-solution" />
              <h2 className="font-semibold">Smart Docs</h2>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleCreateDocument}
              className="flex-1 gap-2"
              size="sm"
              disabled={createDoc.isPending}
            >
              {createDoc.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              New
            </Button>
            <Button
              onClick={() => setTemplateDialogOpen(true)}
              variant="outline"
              className="flex-1 gap-2"
              size="sm"
            >
              <LayoutGrid className="w-4 h-4" />
              Template
            </Button>
          </div>

          {/* Migration notice */}
          {hasLocalDocs && !migrationDone && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center gap-2 text-amber-700 text-xs">
                <Upload className="w-3 h-3" />
                {migrateLocalDocs.isPending ? (
                  <span>Migrating {localDocsCount} docs...</span>
                ) : (
                  <span>{localDocsCount} local docs to migrate</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="text-center py-8 text-text-muted">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
              <p className="text-sm">Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No documents yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {documents.map((doc) => (
                <DocumentListItem
                  key={doc.id}
                  document={doc}
                  isActive={doc.id === currentDocId}
                  onClick={() => setCurrentDocId(doc.id)}
                  onDelete={() => handleDeleteDocument(doc.id)}
                  isDeleting={deleteDoc.isPending}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {currentDocument ? (
          <>
            {/* Document Header */}
            <div className="px-12 py-6 border-b border-warm-200 bg-white">
              <input
                type="text"
                value={currentDocument.title}
                onChange={(e) => handleTitleChange(currentDocument.id, e.target.value)}
                className="font-display text-[28px] font-bold bg-transparent border-none focus:outline-none focus:ring-0 w-full text-text-primary"
                placeholder="Document title..."
              />
              <div className="flex items-center gap-3 mt-3 text-xs text-text-muted">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(currentDocument.updatedAt).toLocaleDateString()}
                </span>
                {currentDocument.detectedEntities.filter(e => e.status === 'pending').length > 0 && (
                  <Badge variant="secondary" className="text-[10px]">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {currentDocument.detectedEntities.filter(e => e.status === 'pending').length} pending entities
                  </Badge>
                )}
              </div>
            </div>

            {/* Editor + Entity Sidebar */}
            <div className="flex-1 flex min-h-0 relative">
              <div className="flex-1 min-w-0">
                <EntityAwareEditor ref={editorRef} documentId={currentDocument.id} />
              </div>

              {/* Unified Entity Sidebar */}
              {!sidebarCollapsed && (
                <UnifiedEntitySidebar
                  documentId={currentDocument.id}
                  tiptapContent={currentDocument.content}
                  onEntityClick={handleEntityClick}
                  onHeadingClick={handleHeadingClick}
                  className="w-[280px]"
                />
              )}

              {/* Sidebar Toggle */}
              <button
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 bg-warm-100 border border-warm-200 rounded-l-md hover:bg-warm-200 transition-colors z-10"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              >
                <ChevronRight
                  className={cn(
                    'w-4 h-4 transition-transform',
                    sidebarCollapsed && 'rotate-180'
                  )}
                />
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-text-muted">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg mb-2">No document selected</p>
              <Button onClick={handleCreateDocument} variant="outline" disabled={createDoc.isPending}>
                {createDoc.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Document
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Template Picker Dialog */}
      <TemplatePickerDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        onSelect={handleTemplateSelect}
        isCreating={createDoc.isPending}
      />
    </div>
  )
}

// Document List Item Component
interface DocumentListItemProps {
  document: SmartDocument
  isActive: boolean
  onClick: () => void
  onDelete: () => void
  isDeleting?: boolean
}

function DocumentListItem({
  document,
  isActive,
  onClick,
  onDelete,
  isDeleting,
}: DocumentListItemProps) {
  const pendingCount = document.detectedEntities.filter(
    (e) => e.status === 'pending'
  ).length

  return (
    <div
      className={cn(
        'group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors',
        isActive
          ? 'bg-white shadow-sm border border-warm-200'
          : 'hover:bg-warm-100'
      )}
      onClick={onClick}
    >
      <FileText className="w-4 h-4 text-text-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{document.title}</p>
        <p className="text-[10px] text-text-muted">
          {new Date(document.updatedAt).toLocaleDateString()}
        </p>
      </div>
      {pendingCount > 0 && (
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {pendingCount}
        </Badge>
      )}
      <button
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-warm-200 transition-all"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        title="Delete document"
        disabled={isDeleting}
      >
        {isDeleting ? (
          <Loader2 className="w-3 h-3 animate-spin text-text-muted" />
        ) : (
          <Trash2 className="w-3 h-3 text-text-muted" />
        )}
      </button>
    </div>
  )
}
